/* Real implementation of ConfigClient against the gateway settings CRUD
 * (/settings/configs). The settings API uses FastAPI's `{ detail }` error shape; we
 * normalize failures to the contract `{ error: { code, message } }` envelope so
 * the shared friendlyError()/toApiError() helpers work uniformly. */

import { authFetchInit } from "../auth/fetch";
import { workspaceHeader } from "../identity/active";
import type { ConfigClient } from "./ConfigClient";
import type {
  ConfigProfile,
  ConfigProfileCreate,
  ConfigProfileUpdate,
  ConfigSchema,
} from "./types";
import type { ApiError, ApiErrorCode } from "../analytics";

function codeForStatus(status: number): ApiErrorCode {
  if (status === 401) return "unauthorized";

  if (status === 403) return "forbidden";

  if (status === 404) return "not_found";

  if (status === 400 || status === 409 || status === 422) return "bad_request";

  return "upstream";
}

/** Turn a settings error body into a human message. The gateway returns
 *  `{ error: "<msg>" }` for business errors (404/409/422) and FastAPI's
 *  `{ detail: ... }` (string, or a list of {loc,msg}) for request-schema
 *  validation — handle both. */
function messageFromDetail(body: unknown, status: number): string {
  const err = (body as { error?: unknown })?.error;
  if (typeof err === "string") return err;

  const detail = (body as { detail?: unknown })?.detail;
  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => (d as { msg?: string }).msg).filter(Boolean);
    if (msgs.length) return msgs.join("; ");
  }

  return `Request failed (HTTP ${status}).`;
}

export class HttpConfigClient implements ConfigClient {
  private readonly baseUrl: string;
  private readonly apiKey?: string;

  /** @param baseUrl gateway settings base, e.g. "http://host:8081/settings/configs". */
  constructor(baseUrl: string, apiKey?: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
    this.apiKey = apiKey;
  }

  private headers(): Record<string, string> {
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
      ...workspaceHeader(),
    };
  }

  private async request<T>(
    path: string,
    init?: RequestInit,
  ): Promise<T | undefined> {
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}${path}`, {
        ...authFetchInit(),
        ...init,
        headers: this.headers(),
      });
    } catch (cause) {
      const error: ApiError = {
        code: "upstream",
        message: "Couldn't reach the config service.",
        detail: String(cause),
      };
      throw { error };
    }
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const error: ApiError = {
        code: codeForStatus(res.status),
        message: messageFromDetail(body, res.status),
        detail: body,
      };
      throw { error };
    }

    if (res.status === 204) return undefined;

    return (await res.json()) as T;
  }

  async list(): Promise<ConfigProfile[]> {
    return (await this.request<ConfigProfile[]>("")) ?? [];
  }

  async getSchema(): Promise<ConfigSchema> {
    return (await this.request<ConfigSchema>("/schema")) as ConfigSchema;
  }

  async get(configId: string): Promise<ConfigProfile> {
    return (await this.request<ConfigProfile>(
      `/${encodeURIComponent(configId)}`,
    )) as ConfigProfile;
  }

  async create(payload: ConfigProfileCreate): Promise<ConfigProfile> {
    return (await this.request<ConfigProfile>("", {
      method: "POST",
      body: JSON.stringify(payload),
    })) as ConfigProfile;
  }

  async update(
    configId: string,
    payload: ConfigProfileUpdate,
  ): Promise<ConfigProfile> {
    return (await this.request<ConfigProfile>(
      `/${encodeURIComponent(configId)}`,
      { method: "PUT", body: JSON.stringify(payload) },
    )) as ConfigProfile;
  }

  async remove(configId: string): Promise<void> {
    await this.request<void>(`/${encodeURIComponent(configId)}`, {
      method: "DELETE",
    });
  }
}
